import { initializeApp, getApps, type FirebaseApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
} from "firebase/messaging";
import { env } from "@/config/env";
import { isFirebaseWebConfigured } from "@/lib/device";

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;
let messagingReady: Promise<Messaging | null> | null = null;

function firebaseConfig() {
  return {
    apiKey: env.firebase.apiKey,
    authDomain: env.firebase.authDomain,
    projectId: env.firebase.projectId,
    storageBucket: env.firebase.storageBucket,
    messagingSenderId: env.firebase.messagingSenderId,
    appId: env.firebase.appId,
  };
}

async function getFirebaseMessaging(): Promise<Messaging | null> {
  if (!isFirebaseWebConfigured()) {
    console.warn(
      "Firebase web not configured (need VITE_FIREBASE_APP_ID + VITE_FIREBASE_VAPID_KEY)",
    );
    return null;
  }
  if (typeof window === "undefined") return null;
  if (messaging) return messaging;
  if (messagingReady) return messagingReady;

  messagingReady = (async () => {
    try {
      const supported = await isSupported();
      if (!supported) return null;
      app = getApps().length ? getApps()[0]! : initializeApp(firebaseConfig());
      messaging = getMessaging(app);
      return messaging;
    } catch (err) {
      console.warn("Firebase messaging unavailable", err);
      return null;
    }
  })();

  return messagingReady;
}

export async function getWebFcmToken(): Promise<string | null> {
  const msg = await getFirebaseMessaging();
  if (!msg || !env.firebase.vapidKey) return null;

  try {
    if (Notification.permission === "default") {
      await Notification.requestPermission();
    }
    if (Notification.permission !== "granted") {
      console.warn("Notification permission not granted; FCM token skipped");
      return null;
    }

    const registration = await navigator.serviceWorker.register(
      "/firebase-messaging-sw.js",
      { scope: "/" },
    );
    await navigator.serviceWorker.ready;

    const token = await getToken(msg, {
      vapidKey: env.firebase.vapidKey,
      serviceWorkerRegistration: registration,
    });
    return token?.trim() || null;
  } catch (err) {
    console.warn("Failed to get FCM web token", err);
    return null;
  }
}

export type IncomingCallPush = {
  callerId: string;
  callerName: string;
  serverCallId: string;
};

export function parseIncomingCallData(
  data: Record<string, string>,
): IncomingCallPush | null {
  const type = data.type || data.messageType || "";
  if (type !== "incomingCall" && !data.incomingCall && !data.incoming_call) {
    return null;
  }

  let callerId = data.caller || "";
  let callerName = "";
  let serverCallId = data.callId || data.serverCallId || "";

  const raw = data.incomingCall || data.incoming_call || "";
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as {
        serverCallId?: string;
        caller?: { id?: string; displayName?: string; phoneNumber?: string };
      };
      serverCallId = parsed.serverCallId || serverCallId;
      callerId = parsed.caller?.phoneNumber || parsed.caller?.id || callerId;
      callerName = parsed.caller?.displayName || "";
    } catch {
      /* ignore malformed payload */
    }
  }

  return {
    callerId: callerId || "unknown",
    callerName: callerName || callerId || "Incoming call",
    serverCallId,
  };
}

/** Foreground FCM (focused tab). */
export async function subscribeIncomingCallPushes(
  onIncoming: (payload: IncomingCallPush) => void,
): Promise<() => void> {
  const msg = await getFirebaseMessaging();
  if (!msg) return () => undefined;

  return onMessage(msg, (payload) => {
    const data = (payload.data || {}) as Record<string, string>;
    const type = data.type || data.messageType || "";
    console.info("FCM foreground message", type || "(no type)", data);
    if (type === "chatMessage") {
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        const title = data.title || payload.notification?.title || "New message";
        const body = data.body || payload.notification?.body || "Papaya chat";
        try {
          new Notification(title, {
            body,
            icon: "/logo.png",
            tag: data.conversationId || "papaya-chat",
          });
        } catch {
          /* ignore */
        }
      }
      return;
    }
    const parsed = parseIncomingCallData(data);
    if (parsed) {
      onIncoming(parsed);
      return;
    }
    // Some browsers only surface notification title/body when focused.
    if (payload.notification?.title || payload.notification?.body) {
      onIncoming({
        callerId: data.caller || "unknown",
        callerName: payload.notification?.title || "Incoming call",
        serverCallId: data.callId || data.serverCallId || "",
      });
    }
  });
}

/**
 * Background / unfocused tab: SW posts here so an open page can still ring.
 * Also handles notificationclick messages.
 */
export function subscribeServiceWorkerCallPushes(
  onIncoming: (payload: IncomingCallPush) => void,
): () => void {
  if (typeof navigator === "undefined" || !navigator.serviceWorker) {
    return () => undefined;
  }

  const handler = (event: MessageEvent) => {
    const type = event.data?.type;
    if (type !== "papaya-incoming-call") return;
    const data = (event.data?.data || {}) as Record<string, string>;
    const parsed = parseIncomingCallData(data);
    if (parsed) onIncoming(parsed);
  };

  navigator.serviceWorker.addEventListener("message", handler);
  return () => navigator.serviceWorker.removeEventListener("message", handler);
}
